import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import LivePreviewPanel, { AboutPreview } from './LivePreviewPanel';
import PageHeroEditor from './PageHeroEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaults = {
  hero: {
    title: 'About',
    highlight: 'PEA PNG',
    description: 'We are the voice of public employees across Papua New Guinea, committed to advocating for fair treatment, professional development, and better working conditions.',
  },
  mission: {
    text: 'To represent and advocate for the rights, welfare, and professional interests of public employees throughout Papua New Guinea, ensuring fair treatment, competitive compensation, and safe working environments for all members.'
  },
  vision: {
    text: 'To be the leading force in shaping a progressive and equitable public service sector in Papua New Guinea, where every employee is valued, empowered, and provided with opportunities for growth and development.'
  },
  values: {
    integrity_title: 'Integrity',
    integrity_text: 'We uphold the highest standards of honesty, transparency, and ethical conduct in all our interactions.',
    unity_title: 'Unity',
    unity_text: 'Together we stand stronger. We believe in the power of collective voice and collaborative action.',
    excellence_title: 'Excellence',
    excellence_text: 'We strive for excellence in representing our members and delivering value through our services.'
  }
};

const AboutContentEditor = () => {
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroContent, setHeroContent] = useState(defaults.hero);
  const [missionContent, setMissionContent] = useState(defaults.mission);
  const [visionContent, setVisionContent] = useState(defaults.vision);
  const [valuesContent, setValuesContent] = useState(defaults.values);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/page-content/about`);
      
      response.data.forEach(item => {
        if (item.section_name === 'hero') {
          setHeroContent({ ...defaults.hero, ...item.content });
        } else if (item.section_name === 'mission') {
          setMissionContent({ ...defaults.mission, ...item.content });
        } else if (item.section_name === 'vision') {
          setVisionContent({ ...defaults.vision, ...item.content });
        } else if (item.section_name === 'values') {
          setValuesContent({ ...defaults.values, ...item.content });
        }
      });
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (sectionName, content) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/page-content/about/${sectionName}`,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${sectionName.charAt(0).toUpperCase() + sectionName.slice(1)} saved successfully`);
    } catch (error) {
      console.error(`Error saving ${sectionName}:`, error);
      toast.error(`Failed to save ${sectionName}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  // Combined content for preview
  const previewContent = {
    hero: heroContent,
    mission: missionContent,
    vision: visionContent,
    values: valuesContent
  };

  return (
    <div className="space-y-6">
      {/* Live Preview Panel */}
      <LivePreviewPanel 
        pageName="about" 
        showPreview={showPreview} 
        setShowPreview={setShowPreview}
      >
        <AboutPreview content={previewContent} />
      </LivePreviewPanel>

      {/* Hero Section */}
      <PageHeroEditor
        title={heroContent.title}
        setTitle={(v) => setHeroContent({ ...heroContent, title: v })}
        highlight={heroContent.highlight}
        setHighlight={(v) => setHeroContent({ ...heroContent, highlight: v })}
        description={heroContent.description}
        setDescription={(v) => setHeroContent({ ...heroContent, description: v })}
        onSave={() => saveSection('hero', heroContent)}
        saving={saving}
      />

      {/* Mission Section */}
      <Card>
        <CardHeader>
          <CardTitle>Mission Statement</CardTitle>
          <CardDescription>Edit your organization's mission</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="mission-text">Mission Text</Label>
            <Textarea
              id="mission-text"
              data-testid="input-mission-text"
              value={missionContent.text}
              onChange={(e) => setMissionContent({ text: e.target.value })}
              rows={4}
              className="mt-1"
            />
          </div>
          <Button
            onClick={() => saveSection('mission', missionContent)}
            data-testid="save-mission-btn"
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} />
            Save Mission
          </Button>
        </CardContent>
      </Card>

      {/* Vision Section */}
      <Card>
        <CardHeader>
          <CardTitle>Vision Statement</CardTitle>
          <CardDescription>Edit your organization's vision</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="vision-text">Vision Text</Label>
            <Textarea
              id="vision-text"
              data-testid="input-vision-text"
              value={visionContent.text}
              onChange={(e) => setVisionContent({ text: e.target.value })}
              rows={4}
              className="mt-1"
            />
          </div>
          <Button
            onClick={() => saveSection('vision', visionContent)}
            data-testid="save-vision-btn"
            disabled={saving}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
          >
            <Save className="mr-2" size={18} />
            Save Vision
          </Button>
        </CardContent>
      </Card>

      {/* Core Values Section */}
      <Card>
        <CardHeader>
          <CardTitle>Core Values</CardTitle>
          <CardDescription>Edit your organization's core values</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Value 1: Integrity */}
          <div className="p-4 bg-orange-50 rounded-lg space-y-3">
            <h4 className="font-semibold text-orange-600">Value 1</h4>
            <div>
              <Label htmlFor="value-1-title">Title</Label>
              <Input
                id="value-1-title"
                data-testid="input-value-1-title"
                value={valuesContent.integrity_title}
                onChange={(e) => setValuesContent({ ...valuesContent, integrity_title: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="value-1-text">Description</Label>
              <Textarea
                id="value-1-text"
                data-testid="input-value-1-text"
                value={valuesContent.integrity_text}
                onChange={(e) => setValuesContent({ ...valuesContent, integrity_text: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>
          </div>

          {/* Value 2: Unity */}
          <div className="p-4 bg-blue-50 rounded-lg space-y-3">
            <h4 className="font-semibold text-blue-600">Value 2</h4>
            <div>
              <Label htmlFor="value-2-title">Title</Label>
              <Input
                id="value-2-title"
                data-testid="input-value-2-title"
                value={valuesContent.unity_title}
                onChange={(e) => setValuesContent({ ...valuesContent, unity_title: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="value-2-text">Description</Label>
              <Textarea
                id="value-2-text"
                data-testid="input-value-2-text"
                value={valuesContent.unity_text}
                onChange={(e) => setValuesContent({ ...valuesContent, unity_text: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>
          </div>

          {/* Value 3: Excellence */}
          <div className="p-4 bg-green-50 rounded-lg space-y-3">
            <h4 className="font-semibold text-green-600">Value 3</h4>
            <div>
              <Label htmlFor="value-3-title">Title</Label>
              <Input
                id="value-3-title"
                data-testid="input-value-3-title"
                value={valuesContent.excellence_title}
                onChange={(e) => setValuesContent({ ...valuesContent, excellence_title: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="value-3-text">Description</Label>
              <Textarea
                id="value-3-text"
                data-testid="input-value-3-text"
                value={valuesContent.excellence_text}
                onChange={(e) => setValuesContent({ ...valuesContent, excellence_text: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>
          </div>

          <Button
            onClick={() => saveSection('values', valuesContent)}
            data-testid="save-values-btn"
            disabled={saving}
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
          >
            <Save className="mr-2" size={18} />
            Save Core Values
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AboutContentEditor;
